import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
import { Store } from "shared/src/stores/Store";
import CustomerSettingsStore from "./settings/CustomerSettingsStore";
import SearchStore from "./SearchStore";
import { PriceConfigStore } from "shared/src/stores/PriceConfigStore";
import { trackBrokenSearchSorting, trackStandardSearchTrigger } from "../utils/SearchTrackingUtil";
import SearchAction from "../actions/SearchAction";
import { cloneDeep, deepEqual } from "shared/src/utils/util";

import {
    parseSortingString,
    sortSearchItems,
    reconstructSearchResult,
    extractSortedPrefix,
    mergeSearchResultItems
} from "../utils/SearchResultSorter";

import { DEFAULT_ROWS, getSearchResultRows, mergeSearchQueries } from "../utils/SearchUtil";

import {
    SearchResultState,
    SearchQuery,
    Pagination,
    PaginationState,
    SearchResultEx,
    SearchResultItem,
    ContextInfo
} from "shared/src/types/articlesearch/SearchResult";

type SearchTriggerTrackingData = {
    suggestionWasAlreadySelected: boolean;
    query: string;
};

const defaultPagination = (): Pagination => {
    return { page: 1, rows: DEFAULT_ROWS };
};

const calculatePageNumber = (actuallyLoadedArticles: number, articlesLoadedPerRow: number): number => {
    return Math.floor(actuallyLoadedArticles / articlesLoadedPerRow);
};

const OVERSAMPLED_SORTING_MODES = ["price"];

type State = {
    searchState: {
        searchQuery: SearchQuery | undefined | null;
        paginationData: PaginationState | undefined | null;
        contextInfo: ContextInfo | undefined | null;
    };
    searchPerformed: boolean; // Track whether we exited the search early due to missing customer data,
    searchResult: SearchResultState | undefined | null;
    // internal pagination: how many pages/rows have we actually retrieved
    internalPagination: Pagination;
    // external pagination: how many pages/rows are we showing to the user
    externalPagination: Pagination;
};

const getInitialState = (): State => {
    return {
        searchState: {
            searchQuery: null,
            paginationData: null,
            contextInfo: null
        },
        searchPerformed: false,
        searchResult: null,
        internalPagination: defaultPagination(),
        externalPagination: defaultPagination()
    };
};

let state: State = getInitialState();

class SearchResultStoreImpl extends Store {
    constructor() {
        super();
        this.listenTo(SearchAction.query, this.doSearch as any);
        this.listenTo(SearchStore, this.handleSearchUpdate as any);
        this.listenTo(CustomerSettingsStore, this.doSearchAgain);
        this.listenTo(PriceConfigStore, this.doSearchAgain);
    }

    getRows(sorting: string | undefined | null): number {
        let rows = getSearchResultRows();
        if (sorting) {
            const [mode] = parseSortingString(sorting);
            if (OVERSAMPLED_SORTING_MODES.includes(mode)) {
                rows = 3 * DEFAULT_ROWS;
            }
        }
        return rows;
    }

    requestIsForAdditionalPage(paginationData: PaginationState, oldPaginationData: PaginationState): boolean {
        const numPagesSearch = paginationData.numPagesSearch || 0;
        return (
            !!oldPaginationData.numPagesSearch &&
            numPagesSearch > 1 &&
            numPagesSearch !== oldPaginationData.numPagesSearch
        );
    }

    setPaginationParamsForInitialSearch(numPagesSearch: number, rowsForFirstInternalPage: number) {
        // If the user is reloading the page when they had already clicked 'load more a few times', we load that many
        // pages immediately. That's the situation where numPagesSearch > 1 (even though this is an 'initial' search).
        state.searchResult = null;
        state.internalPagination = {
            page: 1,
            rows: (numPagesSearch - 1) * getSearchResultRows() + rowsForFirstInternalPage
        };
        state.externalPagination = { page: 1, rows: numPagesSearch * getSearchResultRows() };
    }

    doSearchAgain() {
        if (state.searchState.searchQuery && state.searchState.paginationData) {
            this.doSearch(state.searchState.searchQuery, state.searchState.paginationData);
        }
    }

    getContextDependentAdditionalFilters(): Partial<SearchQuery> {
        if (CustomerSettingsStore.isFsdFilterActive()) {
            return {
                facets: [{ key: "delivery_mode", values: ["METRO_DELIVERY"] }]
            };
        } else {
            return {};
        }
    }

    doSearch(data: SearchQuery, paginationData: PaginationState, trackingData?: SearchTriggerTrackingData) {
        const store = SessionStore.getCustomerStore();
        if (!store) {
            return;
        }

        const queryTermChanged = state?.searchState?.searchQuery?.q !== data.q;
        const contextInfo = {
            selectedStoreId: store,
            locale: SessionStore.getLocale(),
            country: SessionStore.getCountry(),
            customerId: SessionStore.getCustomerId(),
            loggedIn: SessionStore.loggedIn(),
            fsdFilterActive: CustomerSettingsStore.isFsdFilterActive()
        };

        const currentState = state.searchState;
        const pagination = Object.assign({ numPagesSearch: 1 }, paginationData);
        const oldPaginationData = currentState.paginationData || {};

        // Early exit when the request is exactly the same as before and customer is loaded or anonymous search.
        // We need the customer data to decide how the request will look like.
        if (
            state.searchPerformed &&
            deepEqual(data, currentState.searchQuery) &&
            deepEqual(contextInfo, currentState.contextInfo) &&
            pagination.numPagesSearch === oldPaginationData.numPagesSearch
        ) {
            return;
        }

        state.searchState = {
            searchQuery: data,
            paginationData: pagination,
            contextInfo: contextInfo
        };

        const rows = this.getRows(data.sorting);
        const { numPagesSearch } = pagination;

        const isNewSearch =
            !deepEqual(data, currentState.searchQuery) ||
            !this.requestIsForAdditionalPage(pagination, oldPaginationData);
        if (isNewSearch) {
            this.setPaginationParamsForInitialSearch(numPagesSearch, rows);
        } else {
            if (state.internalPagination.rows > rows) {
                state.internalPagination = {
                    page: calculatePageNumber(state.internalPagination.rows, rows),
                    rows: rows
                };
            }
            state.externalPagination = { page: numPagesSearch, rows: getSearchResultRows() };

            const needsMoreResults = this.adjustInternalPagination(state, rows);
            if (!needsMoreResults) {
                this.handleSearchUpdate(SearchStore.getState());
                return;
            }
        }

        if (!CustomerSettingsStore.isReady()) {
            // wait for the customer settings store to become ready (i.e. the customer to be loaded)
            state.searchPerformed = false;
            return;
        }

        const params = this.createSearchParams(state, isNewSearch);
        if (params) {
            state.searchPerformed = true;
            SearchStore.doSearch(params);
        }

        // Only for the search page
        if (trackingData && !trackingData.suggestionWasAlreadySelected) {
            // only track if it has not been tracked as a suggested search yet
            trackStandardSearchTrigger(trackingData.query, queryTermChanged, CustomerSettingsStore.isFsdFilterActive());
        }
    }

    createSearchParams(state: State, isNewSearch: boolean) {
        if (!state.searchState.searchQuery || !state.searchState.contextInfo) {
            return null;
        }
        // FSD filter adjustments for anon users / non-FSD stores
        // CAREFUL: This just manipulates the request, not the state.
        // This is necessary, so that same request (same state) can be done
        // in a store where the customer is FSD (in particular URL stays the same)
        const modifiedQuery = mergeSearchQueries(
            state.searchState.searchQuery,
            this.getContextDependentAdditionalFilters()
        );
        return Object.assign({}, modifiedQuery, state.searchState.contextInfo, {
            page: state.internalPagination.page,
            rows: state.internalPagination.rows,
            more: !isNewSearch
        });
    }

    // Check whether we need more results, and if yes, adjust the internal pagination. (mutates the state variable!)
    adjustInternalPagination(state: State, rows: number) {
        const needsMoreSearchResults = this.needsMoreResults(
            state.searchResult && state.searchResult.result,
            state.externalPagination,
            state.internalPagination,
            rows - getSearchResultRows()
        );
        if (needsMoreSearchResults) {
            state.internalPagination.page += 1;
        }

        return needsMoreSearchResults;
    }

    needsMoreResults(
        result: SearchResultEx | undefined | null,
        externalPagination: Pagination,
        internalPagination: Pagination,
        sortingByPriceBuffer: number
    ) {
        const visibleResults = externalPagination.page * externalPagination.rows;
        const fetchedResults = internalPagination.page * internalPagination.rows;
        const notExhausted = !result || result.amount > fetchedResults;
        return notExhausted && fetchedResults < visibleResults + sortingByPriceBuffer;
    }

    handleSearchUpdate(data: SearchResultState) {
        // Promotion, Category, ReplaceItem, Search are all purposes allowed here.
        const sortedData = this.sortData(cloneDeep(data));
        if (sortedData) {
            state.searchResult = sortedData;
            this.trigger();
        }
    }

    sortData(data: SearchResultState): SearchResultState | undefined | null {
        let shouldTrigger = true;
        if (data) {
            const { sorting, result } = data;
            const storeId = SessionStore.getCustomerStore();
            if (sorting && storeId) {
                const [mode, ascending] = parseSortingString(sorting);
                if (result && result.result) {
                    shouldTrigger = this.sortResult(
                        result,
                        state.searchResult && state.searchResult.result,
                        storeId,
                        state.externalPagination,
                        mode,
                        ascending
                    );
                }
            }
        }
        return shouldTrigger ? data : null;
    }

    sortResult(
        newResult: SearchResultEx,
        oldResult: SearchResultEx | undefined | null,
        storeId: string,
        pagination: Pagination,
        mode: string,
        ascending: boolean
    ): boolean {
        const resultArticles = newResult.result || [];
        const notAllArticlesLoaded = resultArticles.some(
            (articleData) => !articleData || articleData.state === "loading" || articleData.state === "requested"
        );
        // Do not sort by price as long as not all requested article data was loaded.
        if (notAllArticlesLoaded && mode === "price") {
            return false;
        }

        // head contains the results that are fixed and should not be sorted
        // tail contains (All Results - head) and can be sorted
        const oldResultArticles = (oldResult && oldResult.result) || [];
        const [head, tail] = extractSortedPrefix(resultArticles, oldResultArticles, pagination);
        const sortedTail = sortSearchItems(tail, storeId, mode, ascending, PriceConfigStore.getConfig());
        const brokenSorting = sortedTail.filter(
            (item: SearchResultItem) => (item.position || 0) < (pagination.page - 1) * pagination.rows
        ).length;

        if (brokenSorting > 0) {
            const searchResult = state.searchResult || ({} as any);
            trackBrokenSearchSorting(
                searchResult.q,
                searchResult.categoryPath || "UNKNOWN",
                pagination.page,
                brokenSorting
            );
        }

        // merge the fixed head and tail together and only return the visible part based on pagination
        newResult.result = mergeSearchResultItems(head, sortedTail, pagination);
        // above steps mess up some data in the results, fix that here!
        newResult.page = pagination.page;
        newResult.rows = pagination.rows;
        newResult = reconstructSearchResult(newResult);
        return true;
    }

    getState(): SearchResultState | undefined | null {
        return state.searchResult;
    }

    /**
     * For testing purposes.
     */
    resetState() {
        state = getInitialState();
    }
}

export default new SearchResultStoreImpl();
